import type { EmotionCache } from "@emotion/cache"

export type EmotionStyleChunk = {
  key: string
  ids: string[]
  css: string
}

export type EmotionCriticalChunks = {
  html: string
  styles: EmotionStyleChunk[]
}

function isInsertedCssRule(
  value: EmotionCache["inserted"][string]
): value is string {
  return typeof value === "string" && value !== ""
}

function generateStyleTag(cssKey: string, ids: string, styles: string): string {
  return `<style data-emotion="${cssKey} ${ids}">${styles}</style>`
}

export function extractCriticalToChunks(
  html: string,
  cache: EmotionCache
): EmotionCriticalChunks {
  const RGX = new RegExp(`${cache.key}-([a-zA-Z0-9-_]+)`, "gm")
  const result: EmotionCriticalChunks = { html, styles: [] }
  const idsInHtml: Record<string, true> = {}
  let match: RegExpExecArray | null

  while ((match = RGX.exec(html)) !== null) {
    if (idsInHtml[match[1]] === undefined) {
      idsInHtml[match[1]] = true
    }
  }

  const regularCssIds: string[] = []
  let regularCss = ""

  Object.keys(cache.inserted).forEach(id => {
    const inserted = cache.inserted[id]
    const registeredKey = `${cache.key}-${id}`

    if (
      (idsInHtml[id] !== undefined ||
        cache.registered[registeredKey] === undefined) &&
      inserted !== true
    ) {
      if (!isInsertedCssRule(inserted)) {
        return
      }

      if (cache.registered[registeredKey]) {
        regularCssIds.push(id)
        regularCss += inserted
      } else {
        result.styles.push({
          key: `${cache.key}-global`,
          ids: [id],
          css: inserted
        })
      }
    }
  })

  result.styles.push({
    key: cache.key,
    ids: regularCssIds,
    css: regularCss
  })

  return result
}

export function constructStyleTagsFromChunks(
  criticalData: EmotionCriticalChunks
): string {
  let styleTagsString = ""
  criticalData.styles.forEach(item => {
    styleTagsString += generateStyleTag(item.key, item.ids.join(" "), item.css)
  })
  return styleTagsString
}

export default function buildEmotionStyleTagsFromHtml(
  html: string,
  cache: EmotionCache
): string {
  if (cache.compat !== true) {
    cache.compat = true
  }

  return constructStyleTagsFromChunks(extractCriticalToChunks(html, cache))
}
